import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import {
  LayoutDashboard, Radar, Bot, Users2, Film, CalendarClock,
  BarChart3, Rocket, Settings, LogOut, ChevronDown, Sparkles, Building2, Wallet, Shield
} from "lucide-react";
import { useAuth } from "../../lib/auth";
import { Button } from "../ui/button";

// Nav items grouped by required role
// brand_owner & super_admin see the workspace items; only super_admin sees operator items.
const WORKSPACE_NAV = [
  { to: "/app/studio", label: "Creative Studio", icon: Film, testid: "nav-studio" },
  { to: "/app/influencers", label: "Influencers", icon: Sparkles, testid: "nav-influencers" },
  { to: "/app/campaigns", label: "Brands", icon: Rocket, testid: "nav-campaigns" },
  { to: "/app/calendar", label: "Publishing", icon: CalendarClock, testid: "nav-calendar" },
  { to: "/app/wallet", label: "Wallet", icon: Wallet, testid: "nav-wallet" },
  { to: "/app/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
  { to: "/app/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

const ADMIN_NAV = [
  { to: "/app/admin", label: "Command Center", icon: LayoutDashboard, testid: "nav-command-center", end: true },
  { to: "/app/intelligence", label: "Intelligence", icon: Radar, testid: "nav-intelligence" },
  { to: "/app/agents", label: "Agent Registry", icon: Bot, testid: "nav-agents" },
];

export default function AppShell({ children }) {
  const { user, tenant, logout } = useAuth();
  const nav = useNavigate();
  const isSuperAdmin = user?.role === "super_admin";

  const { data: wallet } = useQuery({
    queryKey: ["wallet-balance"],
    queryFn: () => api.get("/wallet/balance").then(r => r.data),
    refetchInterval: 30000,
  });

  return (
    <div className="min-h-screen bg-white text-slate-900 flex">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-slate-200 bg-slate-50 flex flex-col fixed h-screen z-30">
        <div className="px-5 py-5 border-b border-slate-200 flex items-center gap-2">
          <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-sky-500 flex items-center justify-center rounded-lg shadow-sm">
            <span className="font-display font-black text-white text-lg">A</span>
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-[15px] text-slate-900">AIMOS</div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-slate-500">by Neuronex.ai</div>
          </div>
        </div>

        <div className="px-3 py-3 border-b border-slate-200">
          <button
            data-testid="tenant-switcher"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all"
          >
            <Building2 className="w-4 h-4 text-blue-600" />
            <div className="flex-1 text-left">
              <div className="text-xs font-medium text-slate-900 truncate">{tenant?.name || "Workspace"}</div>
              <div className="font-mono text-[9px] uppercase text-slate-500">{tenant?.plan || "starter"} plan</div>
            </div>
            <ChevronDown className="w-3 h-3 text-slate-400" />
          </button>
        </div>

        {/* Wallet chip */}
        <div className="px-3 pt-3">
          <NavLink
            to="/app/wallet"
            data-testid="wallet-chip"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md bg-gradient-to-r from-blue-50 to-sky-50 border border-blue-100 hover:border-blue-300 transition-colors"
          >
            <Wallet className="w-4 h-4 text-blue-600" />
            <div className="flex-1 text-left">
              <div className="text-xs font-bold text-slate-900">{wallet?.balance ?? "—"} tokens</div>
              <div className="font-mono text-[9px] uppercase text-slate-500">{(wallet?.tokens_used_total ?? 0)} used</div>
            </div>
            <span className="font-mono text-[10px] uppercase text-blue-600 font-bold">Top up</span>
          </NavLink>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2">
          <div className="font-mono text-[9px] uppercase tracking-widest text-slate-400 px-3 pb-1.5 pt-1">// Workspace</div>
          {WORKSPACE_NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 mb-0.5 rounded-md text-sm transition-colors duration-150 ${
                    isActive
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-slate-700 hover:bg-blue-50 hover:text-blue-700"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={1.75} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
          {isSuperAdmin && (
            <>
              <div className="font-mono text-[9px] uppercase tracking-widest text-slate-400 px-3 pb-1.5 pt-4 flex items-center gap-1.5">
                <Shield className="w-2.5 h-2.5" /> // Super admin
              </div>
              {ADMIN_NAV.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    data-testid={item.testid}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 mb-0.5 rounded-md text-sm transition-colors duration-150 ${
                        isActive
                          ? "bg-slate-900 text-white shadow-sm"
                          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                      }`
                    }
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.75} />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </>
          )}
        </nav>

        <div className="border-t border-slate-200 p-3 bg-white">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-sm">
              <span className="font-display font-bold text-sm">{(user?.name || "U").charAt(0).toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-slate-900 truncate">{user?.name}</div>
              <div className="font-mono text-[9px] uppercase text-slate-500 truncate">{user?.role}</div>
            </div>
          </div>
          <Button
            data-testid="logout-btn"
            variant="ghost"
            size="sm"
            className="w-full justify-start text-xs h-8 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            onClick={() => { logout(); nav("/login"); }}
          >
            <LogOut className="w-3.5 h-3.5 mr-2" /> Sign out
          </Button>
          <a
            href="https://neuronex.ai"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="powered-by-neuronex"
            className="mt-3 block text-center font-mono text-[9px] uppercase tracking-widest text-slate-400 hover:text-blue-600 transition-colors"
          >
            Powered by <span className="text-slate-600 font-medium">Neuronex.ai</span>
          </a>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 ml-64 min-h-screen bg-white">
        {children}
      </main>
    </div>
  );
}
